import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  cn,
} from "@nexus/ui";
import { toast } from "sonner";
import type { PreflightReportDetail } from "../../types/preflight.types";
import { downloadReportPdf } from "../../api/preflight.api";
import {
  exportReportToCsv,
  exportReportToJson,
  exportReportToText,
} from "../../lib/export-report";
import { exportReportToXlsx } from "../../lib/report-workbook";
import {
  type AssetBundleFormat,
  exportReportAssetsAsZip,
} from "../../lib/export-bundle";

/** Per-asset zip formats, in menu order — paired with their format-label i18n keys. */
const ZIP_FORMATS: ReadonlyArray<[AssetBundleFormat, string]> = [
  ["xlsx", "preflight.export.formatExcel"],
  ["json", "preflight.export.formatJson"],
  ["txt", "preflight.export.formatText"],
];

export interface ExportReportMenuProps {
  report: PreflightReportDetail;
  /** Inline button row (report summary card) vs. compact dropdown (toolbar). */
  layout?: "menu" | "inline";
  className?: string;
}

/** Export menu: server-side PDF, CSV, JSON, Text, and per-asset ZIP bundles. */
export function ExportReportMenu({
  report,
  layout = "menu",
  className,
}: ExportReportMenuProps) {
  const { t } = useTranslation();
  const [pdfPending, setPdfPending] = useState(false);

  async function handlePdf() {
    if (pdfPending) return;
    setPdfPending(true);
    const toastId = toast.loading(t("preflight.export.preparingPdf"));
    try {
      await downloadReportPdf(report.id);
      toast.success(t("preflight.toast.pdfReady"), { id: toastId });
    } catch {
      toast.error(t("preflight.toast.pdfError"), { id: toastId });
    } finally {
      setPdfPending(false);
    }
  }

  if (layout === "inline") {
    return (
      <div className={cn("flex flex-wrap gap-2", className)}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          iconStart="file-text"
          onClick={() => exportReportToCsv(report)}
        >
          {t("preflight.export.csvShort")}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          iconStart="file-text"
          onClick={() => exportReportToJson(report)}
        >
          {t("preflight.export.jsonShort")}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          iconStart="layout-grid"
          onClick={() => exportReportToXlsx(report)}
        >
          {t("preflight.export.excelShort")}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          iconStart="file-text"
          onClick={() => exportReportToText(report)}
        >
          {t("preflight.export.textShort")}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          iconStart="download"
          disabled={pdfPending}
          onClick={() => void handlePdf()}
        >
          {pdfPending ? t("preflight.export.preparingPdf") : t("preflight.export.pdfShort")}
        </Button>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="default" size="sm" iconStart="download">
          {t("preflight.export.trigger")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem disabled={pdfPending} onSelect={() => void handlePdf()}>
          {pdfPending
            ? t("preflight.export.preparingPdf")
            : t("preflight.export.pdf")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => exportReportToCsv(report)}>
          {t("preflight.export.csv")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => exportReportToXlsx(report)}>
          {t("preflight.export.excel")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => exportReportToJson(report)}>
          {t("preflight.export.json")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => exportReportToText(report)}>
          {t("preflight.export.text")}
        </DropdownMenuItem>
        {report.assets.length > 1 ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                {t("preflight.export.eachAsset")}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent sideOffset={4}>
                {ZIP_FORMATS.map(([format, labelKey]) => (
                  <DropdownMenuItem
                    key={format}
                    onSelect={() => exportReportAssetsAsZip(report, format)}
                  >
                    {t(labelKey)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
